import { useState, useEffect } from 'react';
import { Container, Form as BootstrapForm, Button, Row, Col, Card, ListGroup, Alert, Dropdown, DropdownButton } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Form as FormType, Group, Member } from '../../types';
import { fetchFormById, printGroups } from '../../helpers';

type InstructorSubmissionViewProps = {
  refreshForms: () => Promise<void>;
};

const InstructorSubmissionView: React.FC<InstructorSubmissionViewProps> = ({refreshForms}) => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const [form, setForm] = useState<FormType | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [initialGroups, setInitialGroups] = useState<string[]>([]); // To track initial group memberships
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [newGroupTitles, setNewGroupTitles] = useState<{ [groupId: string]: string }>({});
  const isFormClosed = form?.endDate ? new Date(form.endDate).getTime() < Date.now() : false;
  const [roster, setRoster] = useState<Member[]>([]);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const userId = sessionStorage.getItem('userId');
  const API_URL = process.env.REACT_APP_API_URL;
  const userRole = sessionStorage.getItem('role');

  const fetchRosterData = async () => {
      if (!form) return;
      try {
        setLoadingRoster(true);
        setErrorMessage("");

        const response = await fetch(`${API_URL}/api/roster`, { credentials: "include" });
        //console.log("Roster API Response:", response);

        if (response.status === 401) {
        console.error("Unauthorized: Check session or token.");
        setErrorMessage("You are not authorized to view the roster.");
        return; // Exit early if unauthorized
    }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        //console.log("Roster Data:", data);
        //console.log("FORM GROUPS:", form.groups);
        
        if (data.members && Array.isArray(data.members) && form.groups) {
          const formMemberIds = form.groups
          .flatMap((group) => group.members || [])
          .map((member) => member.userId);
          //console.log("Form member ids:", formMemberIds)

          const students = data.members.filter((member: Member) => member.role !== "Instructor");

          const extractNameParts = (fullName: string): { firstName: string; lastName: string } => {
            if (!fullName || typeof fullName !== "string") return { firstName: "", lastName: "" };

            const nameParts: string[] = fullName.trim().split(" ");
            return {
              firstName: nameParts[0] || "",
              lastName: nameParts.length > 1 ? nameParts.slice(-1)[0] : "",
            };
          };

          const formattedRoster: Member[] = students.map((member: { name: string; userId: string; role: string }): Member => {
            const { firstName, lastName } = extractNameParts(member.name);
            return {
              userName: member.name,
              userId: member.userId,
              firstName,
              lastName,
              isPartOfForm: formMemberIds.includes(member.userId),
              role: member.role,
            };
          });

          // Sort by FIRST NAME (handle undefined case)
          formattedRoster.sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""));

          //console.log("FORMATTED ROSTER:", formattedRoster)
          setRoster(formattedRoster);
        } else {
          console.warn("Unexpected roster data format:", data);
          setRoster([]);
        }
      } catch (err) {
        console.error("Error fetching roster data:", err);
        setErrorMessage("Failed to fetch roster data. Please try again later.");
      } finally {
        setLoadingRoster(false);
      }
  };

  useEffect(() => {
    if (form) {
      fetchRosterData();
    }
  }, [form]); // Only fetch the roster when the form is loaded


  // Fetch form data by ID
  useEffect(() => {
      //console.log('sessionStorage contents:', sessionStorage);
    const loadForm = async () => {
      if (!formId) return;
      try {
        const fetchedForm = await fetchFormById(formId);
        if (!fetchedForm) {
        setErrorMessage('Failed to load form data.');
        return;
      }
        //console.log('Loaded Form Data:', fetchedForm);
        setForm(fetchedForm);

        // Preselect user's existing groups
        const userGroups = fetchedForm.groups
          ?.filter((group: Group) =>
            group.members?.some((member) => member.userId === userId)
          )
          .map((group) => group.id);

        setSelectedGroups(userGroups);
        setInitialGroups(userGroups);
      } catch (err) {
        setErrorMessage('Failed to load form data.');
      }
    };
    loadForm();
  }, [formId, userId]);  

  //CONVERTING TO HELPER
  const handleGroupTitleSave = async (groupId: string) => {
    const newTitle = newGroupTitles[groupId];
    if (!newTitle || form?.groups?.find((group) => group.id === groupId)?.name === newTitle) {
      // No change in title, no need to save
      return;
    }

    try {
      // Call the API to save the updated group name
      await fetch(`${process.env.REACT_APP_API_URL}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTitle }),
      });
      //console.log(`Updated group ${groupId} to title: ${newTitle}`);

      // Update the form state to reflect the change
      setForm((prevForm) => {
        if (!prevForm) return prevForm;
        return {
          ...prevForm,
          groups: prevForm.groups?.map((group) =>
            group.id === groupId ? { ...group, name: newTitle } : group
          ),
        };
      });

      // Clear the editing group state
      setEditingGroup(null);
    } catch (err) {
      console.error('Failed to save group title:', err);
      setErrorMessage('Failed to update group title.');
    }
  };
  //CONVERTING TO HELPER
  const handleCancel = () => {
    navigate('/');
  };

  const handleAddAllToGroups = async () => {
    try {
      //console.log("Adding all users to groups...");

      const response = await fetch(`${API_URL}/api/groups/assign-all`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          roster: roster.map((member) => ({
            userId: member.userId,
            userName: member.userName,
          })), // Full roster list from state
          groups: form?.groups || [], // Current form groups from state
        }),
      });
      //console.log("roster data:", roster);
      // Check the response status
      if (!response.ok) {
        throw new Error(`Failed to assign users. Status: ${response.status}`);
      }

      // Parse the response JSON
      const result = await response.json();
      //console.log(result.message); // Log the success message
      //alert(result.message); // Notify the user

      // Reload the page to reflect updated group memberships
      await refreshForms();
      if (formId) {
        const updatedForm = await fetchFormById(formId);
        setForm(updatedForm); // Update state
      }
      await fetchRosterData();
    } catch (err) {
      console.error("Error adding all users to groups:", err);
      alert("Failed to assign users to groups. Please try again.");
    }
  };

  const handleRemoveAllGroupMembers = async () => {
    if (!window.confirm("Are you sure you want to remove all group members?")) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/groups/remove-all`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to remove group members. Status: ${response.status}`);
      }

      const result = await response.json();
      //console.log(result.message);
      //alert(result.message);

      // Safely update form state
      setForm((prevForm) => {
        if (!prevForm) return prevForm; // Prevent null errors
        return {
          ...prevForm,
          groups: prevForm.groups
            ? prevForm.groups.map((group) => ({
                ...group,
                members: [], // Clear all members from each group
              }))
            : [], // Default to an empty array if groups is undefined
        };
      });
      // Refresh the page or re-fetch data to reflect changes
      //await refreshForms();
      await fetchRosterData();
      //window.location.reload(); //was causing entire window to refresh, losing session and causing bad UX
    } catch (err) {
      console.error("Error removing group members:", err);
      alert("Failed to remove group members. Please try again.");
    }
  };

  const handleAssignToGroup = async (userId: string, groupId: string | null) => {
    if (!form) {
      alert("Form data is not loaded. Please try again later.");
      return;
    }

    const member = roster.find((m) => m.userId === userId);
    if (!member) {
      alert("Unable to find the member in the roster.");
      return;
    }

    try {
      // If multiple groups are not allowed, handle removal from the current group
      if (!form?.allowMultipleGroups) {
        const currentGroup = form.groups.find((group) =>
          group.members?.some((m) => m.userId === userId)
        );

        if (currentGroup && currentGroup.id !== groupId) {
          const removeResponse = await fetch(`${API_URL}/api/groups/remove-member`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              groupId: currentGroup.id,
              userId,
            }),
          });

          if (!removeResponse.ok) {
            throw new Error(`Failed to remove user from the current group. Status: ${removeResponse.status}`);
          }
          //console.log(`User removed from group ${currentGroup.name}`);
        }
      }

      // Assign the user to the new group
      const assignResponse = await fetch(`${API_URL}/api/groups/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userId: member.userId,
          groupId,
          userName: member.userName,
        }),
      });

      if (!assignResponse.ok) {
        throw new Error(`Failed to assign user. Status: ${assignResponse.status}`);
      }

      const result = await assignResponse.json();
      //console.log(result.message);
      //alert(result.message);

      // Refresh roster and group data
      if (formId) {
        const updatedForm = await fetchFormById(formId);
        setForm(updatedForm);
      } else {
        console.error("Form ID is undefined.");
      }
      await fetchRosterData();
      //await refreshForms();
    } catch (err) {
      console.error('Error assigning user to group:', err);
      alert('Failed to assign user to group. Please try again.');
    }
  };

  const handleRemoveFromGroup = async (userId: string, groupId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/groups/remove-member`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, groupId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to remove user from group. Status: ${response.status}`);
      }

      const result = await response.json();
      //console.log(result.message);
      //alert(result.message);

      // Refresh group data
      if (formId) {
        const updatedForm = await fetchFormById(formId);
        setForm(updatedForm);
      }
      //await refreshForms();
    } catch (err) {
      console.error('Error removing user from group:', err);
      alert('Failed to remove user from group. Please try again.');
    }
  };

  //CONVERTING TO HELPER
  const isUserAlreadyInGroup = (userId: string): boolean => {
    return (
      form?.groups?.some((g) =>
        g.members?.some((m) => m.userId === userId)
      ) || false
    );
  };

  const handlePrint = () => {
    if (!form) {
      alert("Form data is not loaded.");
      return;
    }
    
    printGroups(form);
  };


  if (!form) {
    return <Container>Loading form data...</Container>;
  }

  return (
    <Container>
        {isFormClosed && (
        <Alert variant="warning">
          This form has closed for submissions as of: {form.endDate ? new Date(form.endDate).toLocaleString() : 'N/A'} local time.
        </Alert>
      )}
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      <h3>{form.title}</h3>
      <p>{form.description}</p>
      <Row>  
      {/* SELECT A GROUP SECTION */}
      <Col md={6}>
        <Card className="mb-3">
          <Card.Header>Join a Group</Card.Header>
          <Card.Body>
            <div className="text-danger text-left mb-2">
              <small>
                {form.allowMultipleGroups
                  ? 'You may select more than one group to join.'
                  : 'Please select one group to join.'}
              </small>
            </div>
            <BootstrapForm>
              <div>
                {form.groups?.slice() 
                  .sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
                  .map((group: Group, index: number) => {
                  const isGroupFull = (group.members?.length || 0) >= group.maxStudents;
                  //const isMember = group.members?.some((member) => member.userId === userId);
                  return (
                    <Card key={index} className="mb-3">
                      <Card.Header>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          {/* Group Name and Max Students */}
                          <div>
                            {editingGroup === group.id ? (
                              <input
                                type="text"
                                value={newGroupTitles[group.id] || group.name}
                                onChange={(e) =>
                                  setNewGroupTitles({
                                    ...newGroupTitles,
                                    [group.id]: e.target.value,
                                  })
                                }
                                onBlur={() => handleGroupTitleSave(group.id)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleGroupTitleSave(group.id);
                                    setEditingGroup(null);
                                  } else if (e.key === "Escape") {
                                    setEditingGroup(null);
                                  }
                                }}
                                autoFocus
                                disabled={isFormClosed}
                              />
                            ) : (
                              <>
                                <strong>{group.name}</strong>{" "}
                                {form.allowEditGroupTitles &&
                                  group.members?.some(
                                    (member) => member.userId === userId
                                  ) && (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => {
                                        setEditingGroup(group.id);
                                        setNewGroupTitles({
                                          ...newGroupTitles,
                                          [group.id]: group.name,
                                        });
                                      }}
                                    >
                                      Edit Title
                                    </Button>
                                  )}
                              </>
                            )}
                            (<span
                              style={{
                                color: isGroupFull ? "red" : "inherit",
                              }}
                            >
                              Max: {group.maxStudents} students
                            </span>)
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                          <ListGroup className="mb-3">
                            {group.members.length > 0 ? (
                              group.members.map((member, memberIndex) => (
                                <ListGroup.Item
                                  key={memberIndex}
                                  style={{
                                    color: isGroupFull ? "grey" : "inherit",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <span>{member.userName}</span>
                                    {userRole === "instructor" && (
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() =>
                                          handleRemoveFromGroup(member.userId, group.id)
                                        }
                                      >
                                        Remove
                                      </Button>
                                    )}
                                  </div>
                                </ListGroup.Item>
                              ))
                            ) : (
                              <ListGroup.Item>
                                <div
                                  style={{
                                    textAlign: "center",
                                    fontStyle: "italic",
                                    color: "gray",
                                  }}
                                >
                                  No group members
                                </div>
                              </ListGroup.Item>
                            )}
                          </ListGroup>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </BootstrapForm>
          </Card.Body>
        </Card>
      </Col>
        {/* INSTRUCTOR TOOLS */}
        <Col md={6}>
        <Container>
          <Container>
            <Row className="mb-2">
              {/*<h3>Toolbar:</h3>*/}
            </Row>
            <Row className="mb-3">
              <Col xs="auto">
              <Button variant="primary" onClick={handleAddAllToGroups}>
                Assign all users
              </Button>
            </Col>
              <Col xs="auto">
                <Button variant="danger" onClick={handleRemoveAllGroupMembers} className="mt-sm-2 mt-lg-0 mt-xl-0">
                  Remove all users
                </Button>
              </Col>
              <Col xs="auto">
                <Button variant="outline-primary" onClick={handlePrint} className="mt-2 mt-xl-0">
                  🖨 Print Groups
                </Button>
              </Col>
            </Row>
          </Container>
          <Container>
          <h4>Class Roster</h4>
          {loadingRoster ? (
            <p>Loading roster...</p>
          ) : roster.length > 0 ? (
            <ListGroup>
              {roster.map((member, memberIndex) => (
                <ListGroup.Item key={memberIndex}>
                <div   
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-around",
                  gap: "10px",
                }}>
                  {member.userName}
                  {member.isPartOfForm && (
                    <span
                      style={{
                        marginLeft: "8px",
                        color: "green",
                        fontWeight: "bold",
                      }}
                    >
                      Completed
                    </span>
                  )}
                  {!member.isPartOfForm && (
                    <span
                      style={{
                        marginLeft: "8px",
                        color: "red",
                        fontWeight: "bold",
                      }}
                    >
                      Not Completed
                    </span>
                  )}
                  <DropdownButton
                      id={`dropdown-${member.userId}`}
                      title="Assign to Group"
                      variant="secondary"
                      size="sm"
                    >
                      {form.groups.map((group) => (
                        <Dropdown.Item
                          key={group.id}
                          onClick={() => handleAssignToGroup(member.userId, group.id)}
                          disabled={(!form.allowMultipleGroups && isUserAlreadyInGroup(member.userId)) || (group.members?.length || 0) >= group.maxStudents}
                        >
                          Assign to {group.name} {(!form.allowMultipleGroups && isUserAlreadyInGroup(member.userId)) && "(Already in a group)"}
                        </Dropdown.Item>
                      ))}
                    </DropdownButton>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <p>No members found for this course.</p>
          )}
          </Container>
           </Container>
        </Col>    
      </Row>
      {/* FORM SUBMISSION OPTIONS */}
      <Row className="mt-3 justify-content-center">
        <Col xs="auto">
          <Button variant="secondary" onClick={handleCancel}>
            Return to main screen
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default InstructorSubmissionView;
